import {
  useV2CreateQuestionnaireMutation,
  useV2UpdateQuestionnaireMutation,
  useV2DeleteQuestionnaireMutation,
  useV2DuplicateQuestionnaireMutation,
} from "@generated";
import type { V2QuestionnaireResource } from "@generated";
import type { NewQuestionnaireValues, SurveyFormValues } from "../types";

// Build the JSON:API `data` body for create/update. `account` is inferred from
// auth server-side, so we omit it and cast (same pattern as Currencies).
// NOTE on wire keys: `thank_you` is snake_case; `completion-url`/`tag-list` are
// kebab. `teams` is a writable computed attribute on QuestionnaireResource with
// a custom setter: `def teams=(team_attrs)` → `@model.team_ids = team_attrs.map { |t| t['id'] }`.
// We therefore send `teams: [{id}]` objects (string ids); the resource setter
// extracts the ids and assigns them via the model's `team_ids=` writer.
export const buildQuestionnaireData = (
  values: SurveyFormValues,
  id?: string
): V2QuestionnaireResource => {
  return {
    type: "questionnaires",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      published: values.published,
      introduction: values.introduction,
      thank_you: values.thankYou,
      "completion-url": values.redirect ? values.completionUrl : "",
      "tag-list": values.tagList,
      teams: values.teamIds.map((tid) => ({ id: tid })),
    },
  } as unknown as V2QuestionnaireResource;
};

// Minimal create payload from the New page (name + published only).
export const buildNewQuestionnaireData = (
  values: NewQuestionnaireValues
): V2QuestionnaireResource =>
  ({
    type: "questionnaires",
    attributes: { name: values.name, published: values.published },
  } as unknown as V2QuestionnaireResource);

export const useQuestionnaireMutations = () => {
  const [create, createState] = useV2CreateQuestionnaireMutation();
  const [update, updateState] = useV2UpdateQuestionnaireMutation();
  const [destroy, destroyState] = useV2DeleteQuestionnaireMutation();
  const [dupe, duplicateState] = useV2DuplicateQuestionnaireMutation();

  return {
    create: (values: NewQuestionnaireValues) =>
      create({ body: { data: buildNewQuestionnaireData(values) } }).unwrap(),

    update: (id: string, values: SurveyFormValues) =>
      update({
        id: id,
        body: { data: buildQuestionnaireData(values, id) },
      }).unwrap(),

    archive: (id: string) => destroy({ id: id }).unwrap(),

    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "questionnaires",
            id: id,
            attributes: { name, active: true },
          } as unknown as V2QuestionnaireResource,
        },
      }).unwrap(),

    duplicate: (id: string) => dupe({ id }).unwrap(),

    states: { createState, updateState, destroyState, duplicateState },
  };
};

export default useQuestionnaireMutations;
